import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { BuildInfo } from "@/components/BuildInfo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { getApiErrorMessage } from "@/lib/api";
import { setPassword as setPasswordApi, toAccount } from "@/lib/authApi";
import { useApp } from "@/store/useApp";
import { homeRouteFor } from "@/lib/types";

const MIN_PASSWORD_LENGTH = 6;

/**
 * Criação/troca de senha da conta AUTENTICADA. A identidade já foi provada (login por senha ou sessão
 * Google) — nunca pelo e-mail. Conta sem senha (só-Google) cria a primeira; conta com senha informa a
 * atual. Entradas: troca forçada pós-login (passwordResetRequired) ou botão voluntário no perfil.
 */
export default function ForcePasswordReset() {
  const navigate = useNavigate();
  const account = useApp((s) => s.account);
  const authStatus = useApp((s) => s.authStatus);
  const setAccount = useApp((s) => s.setAccount);
  const [current, setCurrent] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Sem sessão não há conta a redefinir — a identidade vem da sessão, não da URL.
  useEffect(() => {
    if (authStatus === "ready" && !account) {
      navigate("/login", { replace: true });
    }
  }, [authStatus, account, navigate]);

  if (!account) return null;

  const needsCurrent = account.hasPassword === true;

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const currentValue = (form.elements.namedItem("current-password") as HTMLInputElement | null)?.value ?? current;
    const passwordValue = (form.elements.namedItem("new-password") as HTMLInputElement | null)?.value ?? password;
    const confirmValue = (form.elements.namedItem("confirm-password") as HTMLInputElement | null)?.value ?? confirm;

    if (needsCurrent && !currentValue) {
      toast.error("Informe sua senha atual");
      return;
    }
    if (passwordValue.length < MIN_PASSWORD_LENGTH) {
      toast.error(`A senha precisa ter pelo menos ${MIN_PASSWORD_LENGTH} caracteres`);
      return;
    }
    if (passwordValue !== confirmValue) {
      toast.error("As senhas não conferem");
      return;
    }
    setIsSubmitting(true);
    try {
      const updated = toAccount(await setPasswordApi(needsCurrent ? currentValue : null, passwordValue));
      setAccount(updated);
      toast.success("Senha definida com sucesso.");
      navigate(homeRouteFor(updated), { replace: true });
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Não foi possível definir a senha"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-sm space-y-8">
          <Logo />
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 rounded-full border bg-secondary/40 px-3 py-1.5 text-xs font-medium text-muted-foreground">
              <ShieldCheck className="h-3.5 w-3.5" />
              {needsCurrent ? "Alterar senha" : "Criar senha"}
            </div>
            <h2 className="font-display text-3xl font-bold tracking-tight">
              {needsCurrent ? "Defina uma nova senha" : "Crie uma senha de acesso"}
            </h2>
            <p className="text-sm text-muted-foreground">
              Para a conta <span className="font-medium text-foreground">{account.email}</span>.
              {needsCurrent
                ? " Informe a senha atual e escolha a nova."
                : " Você poderá continuar entrando com o Google e, a partir de agora, também por senha."}
            </p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            {needsCurrent && (
              <div className="space-y-2">
                <Label htmlFor="current-password">Senha atual</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="current-password"
                    name="current-password"
                    type="password"
                    autoComplete="current-password"
                    placeholder="••••••••"
                    value={current}
                    onChange={(e) => setCurrent(e.target.value)}
                    className="pl-9 h-11"
                  />
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="new-password">Nova senha</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="new-password"
                  name="new-password"
                  type="password"
                  autoComplete="new-password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-9 h-11"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirmar nova senha</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="confirm-password"
                  name="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  placeholder="••••••••"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="pl-9 h-11"
                />
              </div>
            </div>

            <Button type="submit" variant="hero" size="lg" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? "Salvando..." : "Salvar senha"}
              {!isSubmitting && <ArrowRight className="h-4 w-4" />}
            </Button>
          </form>

          <div className="text-sm text-muted-foreground text-center">
            <Link to={homeRouteFor(account)} className="font-semibold text-primary hover:underline">
              Voltar
            </Link>
          </div>
        </div>
      </div>
      <footer className="py-4 px-6">
        <BuildInfo />
      </footer>
    </div>
  );
}
